/**
 * Where a visitor came from, captured once per session.
 *
 * This cannot be derived on the server. The audit request is a same-origin
 * fetch, so its `referer` header is always interfaceaudit.com and the real
 * source is already gone by the time the API sees it. It also cannot be
 * recovered later: a scan that runs unattributed stays unattributed forever,
 * which is why capture happens on the FIRST page load rather than at submit.
 * By submit time the referrer is whatever internal page the visitor came from.
 */

const KEY = 'ia_attribution';

export interface Attribution {
  referrer: string | null;
  landingPath: string | null;
  utm: {
    source: string | null;
    medium: string | null;
    campaign: string | null;
    term: string | null;
    content: string | null;
  };
  capturedAt: string;
}

/** Hostname only, so we never persist a full third-party URL with its query. */
function referrerHost(): string | null {
  if (!document.referrer) return null;
  try {
    const host = new URL(document.referrer).hostname;
    return host === window.location.hostname ? null : host;
  } catch {
    return null;
  }
}

/**
 * Records the source on first load and keeps it for the session. Later calls
 * are no-ops, so an internal navigation cannot overwrite the original source
 * with an interfaceaudit.com referrer.
 */
export function captureAttributionOnce(): void {
  if (typeof window === 'undefined') return;
  try {
    if (sessionStorage.getItem(KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const get = (k: string) => params.get(k) || null;
    const attribution: Attribution = {
      referrer: referrerHost(),
      landingPath: window.location.pathname || null,
      utm: {
        source: get('utm_source'),
        medium: get('utm_medium'),
        campaign: get('utm_campaign'),
        term: get('utm_term'),
        content: get('utm_content'),
      },
      capturedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(KEY, JSON.stringify(attribution));
  } catch {
    // Private mode, blocked storage: attribution is best-effort and must never
    // interfere with running the audit.
  }
}

/** The stored source, or null when it could not be captured. */
export function getAttribution(): Attribution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}
